import {html, LitElement} from 'lit';
import {customElement, state, property} from 'lit/decorators.js';
import {TariffFormStore} from './../Storage/tariffStorage';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';

import {formatNumber, getLocallangText} from '../swsRateCalculatorForm-init.mjs';
import {formatDate} from '../utils';

interface Tariff {
    title: string;
    title_long: string;
    media: string;
    zips: number[];
    icon: string;
    description_short: string;
    description: string;
    description_long: string;
    labor_price: number;
    base_price: any[];
    type: number;
    faqs: {
        title: string,
        answer: string
    };
    meta: {
        customerType: number,
    }
}

interface SelectedTariff {
    type: number;
    tariff: Tariff;
}

interface Reward {
    uid: number;
    title: string;
    media: string;
    description: string;
    // ggf. weitere Felder ergänzen
}

@customElement('sws-summary')
export class SwsSummary extends LitElement {

    @state()
    private tariffs: SelectedTariff[] = [];

    @state()
    private data: Record<string, any> = {};

    @state()
    private selectedRewardUid: number | null = null;

    @property({type: Array})
    tabs: { id: string; title: string; data: any; deliveryData?: Record<string, any> }[]

    @property({type: Array})
    rewards: Reward[] = [];

    connectedCallback() {
        super.connectedCallback();
    }

    public updateElement() {
        this.requestUpdate();
    }

    protected createRenderRoot() {
        return this;
    }

    private changeDate(tariff) {
        const date = tariff.deliveryData.dateToTerminateContract;
        const [year, month, day] = date.split('-');
        const europeanDate = `${day}.${month}.${year}`;
        return europeanDate;
    }

    private openModal(tariff: any, index: number): void {

        const event = new CustomEvent('set-tariff-data', {
            detail: {
                productType: Number(tariff.data.type),
                productName: tariff.title,
                tariffData: tariff.data,
                deliveryData: tariff?.deliveryData || null,
                index: index,
            },
            bubbles: true,
            composed: true
        });
        document.dispatchEvent(event); // globale Event-Übertragung
    }

    private openModalDetails(tariff: any): void {
        const titleEl = document.getElementById('modalTariffTitle');
        const headerEl = document.getElementById('modalTariffHeader');
        const detailsEl = document.getElementById('modalTariffDetails');

        const langString = TYPO3?.lang?.['modal.tariffDetails.header'] || '';
        const interpolatedHeader = langString.replace('{{title}}', tariff.title);

        if (titleEl) titleEl.textContent = tariff.data.tariff.title;
        if (headerEl) headerEl.innerHTML = interpolatedHeader;
        if (detailsEl) detailsEl.innerHTML = tariff.data.tariff.description_short;
    }

    private _handleRewardChange(e: Event, uid: string) {
        const target = e.target as HTMLInputElement;

        if (target.checked) {
            // Checkbox angehakt -> andere deaktivieren
            this.selectedRewardUid = uid;

            // Alle Checkboxen außer die aktuelle zurücksetzen
            const checkboxes = this.renderRoot.querySelectorAll('.checkbox-target');
            checkboxes.forEach(cb => {
                if ((cb as HTMLInputElement).id !== `reward-${uid}`) {
                    (cb as HTMLInputElement).checked = false;
                }
            });
        } else {
            // Checkbox wieder abgewählt -> keine Auswahl
            this.selectedRewardUid = null;
        }

        this.requestUpdate();
    }


    render() {
        const tariffs = TariffFormStore.getAll() || [];

        return html`
            <div class="bg-gradient-sws">
                <div class="container">
                    <div class="row justify-content-center">
                        <div class="col-12 col-lg-8 my-4">
                            <div class="bg-light-dark p-3-75 pt-2-375 pb-2-375">
                                <h2>${TYPO3?.lang?.[`form.configureTariff.summary.header`]}</h2>
                            </div>
                            <div class="bg-white p-3-75 pt-2-375 pb-2 m-auto">
                                <div class="card bg-white border-0">
                                    <div class="card-body flex-column p-0 d-flex justify-content-between align-items-center">
                                        <p class="w-100">${unsafeHTML(TYPO3?.lang?.[`form.configureTariff.summary.infoText`])}</p>
                                        <h5 class="w-100 pt-5 pb-3 fw-bold">
                                            ${TYPO3?.lang?.[`form.configureTariff.summary.tariff.headline`]}</h5>
                                        <div class="tariffs-wrapper w-100">
                                            ${this.renderTariffs()}
                                            <div class="w-100 d-flex mb-4 align-items-end">
                                                <h5 class="fw-bold m-0 me-4">${TYPO3?.lang?.[`form.configureTariff.summary.tariff.addTariffs.label`]}</h5>
                                            </div>
                                            ${TYPO3?.lang?.[`form.configureTariff.summary.tariff.addTariffs.info`]}
                                            <button id="sws-rateCalculator-add-tariffs"
                                                    class="btn btn-add font-18 mb-5 mt-3 d-flex align-items-center"><i
                                                    class="icon-plus icon-plus-tariff-form pe-2"></i>
                                                ${TYPO3?.lang?.[`btn.newTariffs`]}
                                            </button>
                                        </div>
                                        ${this.renderUserData()}
                                        ${this.renderPaymentData()}
                                        ${this.renderHeatPumpAgreement()}
                                        ${this.renderModul2Agreement()}
                                        ${this.renderRewards()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    private renderTariffs() {
        this.requestUpdate();

        const tariffs = TariffFormStore.get('allTariffs') || [];

        return html`
            ${tariffs.map((tariff: any, index) =>
                    html`
                        <div class="w-100 summary-card mb-5">
                            <div class="w-100 pe-3 p-md-0 summary-tariff-header d-flex flex-column flex-md-row ">
                                <div class="d-flex align-items-center me-3">
                                    <img src="${tariff.data.tariff.icon}" width="57"
                                         height="57"
                                         class="me-2 max-w-none"
                                         alt="Tarif-Icon">
                                    <strong class="font-20 font-sum-header py-3">${tariff.title}</strong>

                                </div>
                                <div class="w-100 p-3 p-md-0 d-flex justify-content-between align-items-center">
                                    <div class="d-flex flex-column right-align order-md-2 pe-2 mt-1">
                                        ${tariff.deliveryData.selectedSwitchMeterNumber === 'know-meter-number' || tariff.deliveryData.selectedSwitchMeterNumber === undefined ? html`
                                            ${(tariff.deliveryData.allMeterCounter && tariff.deliveryData.maloCounter) || (tariff.deliveryData.maloCounter) ? html`
                                                <p class="text-black order-md-2 m-0 px-md-2">
                                                    ${TYPO3?.lang?.[`form.configureTariff.summary.tariff.maloCounter`]}:&nbsp;
                                                    <strong>${tariff.deliveryData.maloCounter}</strong></p>` : html`
                                                <p class="text-black order-md-2 m-0 px-md-2">
                                                    ${TYPO3?.lang?.[`form.configureTariff.summary.tariff.counter`]}:&nbsp;
                                                    <strong>${tariff.deliveryData.allMeterCounter}</strong></p>
                                            `}
                                        `: html `
                                            <p class="text-black order-md-2 m-0 px-md-2">
                                                ${TYPO3?.lang?.[`form.configureTariff.summary.tariff.shareMeterNumber`]}
                                            </p>
                                        `}
                                    </div>
                                    <a href="#" class="text-black mt-1 order-md-1" data-bs-toggle="modal"
                                       data-bs-target="#modal-change-tariff" aria-label="${getLocallangText('link.customize.ariaLabel', [tariff.title])}"
                                       @click=${() => this.openModal(tariff, index)}>${TYPO3?.lang?.[`link.customize`]}</a>
                                </div>
                            </div>


                            <div class="card-body">
                                ${tariff.deliveryData.allMeterCounter && tariff.deliveryData.maloCounter ? html`
                                    <div
                                            class="d-flex flex-column flex-md-row justify-content-md-between pb-3 mb-4 border-bottom-black">
                                        <p class="text-black mb-0">
                                            ${TYPO3?.lang?.[`form.configureTariff.summary.tariff.alternativeCounter`]}</p>
                                            <strong>${tariff.deliveryData.allMeterCounter}</strong>
                                    </div>` : html``}
                                <div
                                        class="d-flex flex-column flex-md-row justify-content-md-between pb-3 mb-4 border-bottom-black">
                                    <p class="text-black mb-0 align-self-start align-self-md-center">
                                        ${TYPO3?.lang?.[`form.configureTariff.summary.tariff.monthly`]}</p>
                                    <strong class="font-24">${formatNumber(tariff.data.tariff.price, 2)} €</strong>
                                </div>
                                <div
                                        class="d-flex flex-column flex-md-row justify-content-md-between pb-3 mb-4 border-bottom-black">
                                    <p class="text-black mb-0">
                                        ${TYPO3?.lang?.[`form.configureTariff.summary.tariff.consumption`]}</p>
                                    <strong>${formatNumber(tariff.data.tariff.consumptionGeneral)} ${TYPO3?.lang?.[`form.configureTariff.summary.tariff.consumption.unit`]}</strong>
                                </div>
                                <div
                                        class="d-flex flex-column flex-md-row justify-content-md-between pb-3 mb-3 border-bottom-black">
                                    <p class="text-black mb-0">
                                        ${TYPO3?.lang?.[`form.configureTariff.summary.tariff.minimumTerm.headline`]}</p>
                                    ${tariff.data.tariff.minimum_term === 0 ? html`
                                        <strong>${TYPO3?.lang?.[`form.configureTariff.summary.tariff.minimumTerm.no`]}</strong>` : html`
                                        <strong>${tariff.data.tariff.minimum_term} ${TYPO3?.lang?.[`form.configureTariff.summary.tariff.minimumTerm.month`]}</strong>`}
                                </div>

                                <!-- Link trigger modal -->

                                <a href="#" class="text-black" data-bs-toggle="modal"
                                   data-bs-target="#modal-tariff-details" @click=${() => this.openModalDetails(tariff)}>${TYPO3?.lang?.[`form.configureTariff.summary.tariff.seeDetails`]}</a>

                                <div
                                        class="d-flex flex-column flex-md-row justify-content-md-between pb-3 mb-4 pt-3-75 border-bottom-black">
                                    <p class="text-black mb-0">
                                        ${TYPO3?.lang?.[`form.configureTariff.summary.tariff.reason`]}</p>

                                    ${tariff.deliveryData.selectedReason === "move" ? html`
                                        <strong>${TYPO3?.lang?.[`form.configureTariff.summary.tariff.move`]}</strong>` : html`
                                        <strong>${TYPO3?.lang?.[`form.configureTariff.summary.tariff.supplierChange`]}</strong>`}
                                </div>
                                ${tariff.title === "ÖkostromDyna" ? html`
                                    <div
                                            class="d-flex flex-column flex-md-row justify-content-md-between pb-3 mb-4 border-bottom-black">
                                        <p class="text-black mb-0">
                                            ${TYPO3?.lang?.[`form.configureTariff.summary.tariff.smartMeter.headline`]}</p>
                                        ${tariff.deliveryData?.smartMeterOption === "ownSmartMeter" ? html`
                                            <strong class="w-md-75 text-md-end">${TYPO3?.lang?.[`form.configureTariff.summary.tariff.smartMeter.alreadyExist`]}</strong>` : html`
                                            <strong class="w-md-75 text-md-end">${TYPO3?.lang?.[`form.configureTariff.summary.tariff.smartMeter.buyNew`]}</strong>`}
                                    </div>` : html``}
                                <div
                                        class="d-flex flex-column flex-md-row justify-content-md-between pb-3 mb-4 border-bottom-black">
                                    <p class="text-black mb-0">
                                        ${TYPO3?.lang?.[`form.configureTariff.summary.tariff.previousSupplier`]}</p>
                                    <strong>${tariff.deliveryData.previousSupplier}</strong>
                                </div>
                                <div
                                        class="d-flex flex-column flex-md-row justify-content-md-between ${(tariff.title === "ÖkostromDyna") || (tariff.deliveryData?.selectedTerminationType === "self") ? "pb-3 mb-4 border-bottom-black" : "mb-1"}">
                                    <p class="text-black mb-0">
                                        ${TYPO3?.lang?.[`form.configureTariff.summary.tariff.termination.headline`]}</p>
                                    ${tariff.deliveryData?.selectedTerminationType === "self" ? html`
                                        <strong class="w-md-75 text-md-end">${TYPO3?.lang?.[`form.configureTariff.summary.tariff.termination.alreadyTerminate`]}</strong> ` : html`
                                        <strong class="w-md-75 text-md-end">${TYPO3?.lang?.[`form.configureTariff.summary.tariff.termination.terminateBySWS`]}</strong>`}
                                </div>
                                ${tariff.deliveryData?.selectedTerminationType === "self" ? html`
                                    <div
                                            class="d-flex flex-column flex-md-row justify-content-md-between ${tariff.title === "ÖkostromDyna" ? "pb-3 mb-4 border-bottom-black" : "mb-1"}">
                                        <p class="text-black mb-0">
                                            ${TYPO3?.lang?.[`form.configureTariff.summary.tariff.termination.date`]}</p>
                                        <strong>${this.changeDate(tariff)}</strong>
                                    </div>` : html``}
                                ${tariff.title === "ÖkostromDyna" ? html`
                                    <div class="mb-3 d-flex flex-column">
                                    <div class="form-check d-flex align-items-center">
                                        <input class="form-check-input checkbox mt-0" type="checkbox"
                                               id="tariffAgreeCheck">
                                        <label class="form-check-label ps-2" for="tarifAgreeCheck">
                                            ${TYPO3?.lang?.[`form.configureTariff.summary.tariff.dyna.agreement`]}
                                        </label>
                                    </div>
                                    <div class="invalid-feedback" id="dyna-error"></div></div>
                                ` : html``}
                            </div>
                        </div>
                    `)}
        `;
    }

    private renderUserData() {
        this.requestUpdate();

        const userData = TariffFormStore.get('userData') || {};
        const tariffs = TariffFormStore.get('allTariffs') || {};
        const customerType = tariffs[0]?.data?.tariff?.meta?.customerType;
        return html`
            <div class="w-100 d-flex mb-4 align-items-end">
                <h5 class="fw-bold m-0 me-4">${TYPO3?.lang?.[`form.configureTariff.summary.userData.headline`]}</h5>
                <a href="#"
                   id="customize-userData-link"
                   class="text-black order-md-1" aria-label="${TYPO3?.lang?.[`link.userData.customize.ariaLabel`]}">
                    ${TYPO3?.lang?.[`link.customize`]}
                </a>

            </div>
            <div class="w-100 card-body summary-card p-4 mb-5">
                <div>
                    <h6 class="font-18 fw-bold m-0">${TYPO3?.lang?.[`form.configureTariff.summary.userData.contact`]}</h6>
                    <ul class="list-unstyled m-0 p-0">
                        ${customerType === '0' ? html`
                        <li class="font-18 p-0">
                            ${this._setSalutation(userData['sws-rateCalculator-contact-details-salutation'])}
                            ${userData['sws-rateCalculator-contact-details-first-name']}
                            ${userData['sws-rateCalculator-contact-details-last-name']}
                        </li>
                        ` : html `
                            <li class="font-18 p-0">${userData['sws-rateCalculator-contact-details-company']}
                            </li>
                            ${userData['sws-rateCalculator-contact-details-contact-person'] !== "" ? html`
                            <li class="font-18 p-0">${userData['sws-rateCalculator-contact-details-contact-person']}
                            </li>` : html``}`}
                        <li class="font-18 p-0">${formatDate(userData['sws-rateCalculator-contact-details-birthday'])}</li>
                        <li class="font-18 p-0">${userData['sws-rateCalculator-contact-details-mail']}</li>
                        <li class="font-18 p-0">${userData['sws-rateCalculator-contact-details-phone']}</li>
                    </ul>
                </div>

                <div class="w-100 py-4">
                    <h5 class="font-18 fw-bold m-0">${TYPO3?.lang?.[`form.configureTariff.summary.userData.delivery`]}</h5>

                    <ul class="list-unstyled p-0 m-0">
                        <li class="font-18 p-0">${userData['sws-rateCalculator-contact-details-street']}
                            ${userData['sws-rateCalculator-contact-details-house-number']}
                        </li>
                        <li class="font-18 p-0">${userData['sws-rateCalculator-contact-details-postal-code']}
                            ${userData['sws-rateCalculator-contact-details-city']}
                        </li>
                    </ul>
                </div>
                <div>
                    <h5 class="font-18 fw-bold m-0">
                        ${TYPO3?.lang?.[`form.configureTariff.summary.userData.billing.headline`]}</h5>
                    <ul class="list-unstyled p-0 m-0">
                        ${userData['sws-rateCalculator-contact-details-cc-additional-address'] === true ? html`
                            <li class="font-18 p-0">${userData['sws-rateCalculator-contact-details-cc-street']}
                                ${userData['sws-rateCalculator-contact-details-cc-house-number']}
                            </li>
                            <li class="font-18 p-0">${userData['sws-rateCalculator-contact-details-cc-postal-code']}
                                ${userData['sws-rateCalculator-contact-details-cc-city']}
                            </li> ` : html`
                            <li class="font-18 p-0">
                                ${TYPO3?.lang?.[`form.configureTariff.summary.userData.billing.same`]}
                            </li>`}
                    </ul>
                </div>
            </div>
        `;
    }

    private renderPaymentData() {
        this.requestUpdate();

        const paymentData = TariffFormStore.get('paymentData') || {};
        return html`
            <div class="w-100 d-flex mb-4 align-items-end">
                <h5 class="fw-bold m-0 me-4">${TYPO3?.lang?.[`form.configureTariff.summary.payment.headline`]}</h5>
                <a href="#"
                   id="customize-payment-link"
                   class="text-black order-md-1" aria-label="${TYPO3?.lang?.[`link.payment.customize.ariaLabel`]}">
                    ${TYPO3?.lang?.[`link.customize`]}
                </a>

            </div>

            <div class="w-100 card-body summary-card mb-5 p-0">

                <div class="w-100 p-4">
                    ${paymentData['sws-rateCalculator-billing-direct-debit-mandate'] === 'direct-debit-mandate' ? html`
                        <h5 class="font-18 fw-bold">
                            ${TYPO3?.lang?.[`form.configureTariff.summary.payment.directDebet`]}</h5>
                        <ul class="list-unstyled p-0 m-0">
                            <li class="font-18 p-0">${paymentData['sws-rateCalculator-billing-details-cc-name']}</li>
                            <li class="font-18 p-0">${paymentData['sws-rateCalculator-billing-details-cc-number']}</li>
                            <li class="font-18 p-0">${paymentData['sws-rateCalculator-billing-details-cc-type']}</li>
                        </ul>
                    ` : html`
                        <h5 class="font-18 fw-bold mb-0">
                            ${TYPO3?.lang?.[`form.configureTariff.summary.payment.selfPay`]}</h5>
                    `}
                </div>
            </div>
        `;
    }

    private renderHeatPumpAgreement() {
        const tariffs = TariffFormStore.get('allTariffs') || [];
        const hasHeatPump = tariffs.some((tariff: any) => tariff.data?.tariff?.type === 1);

        if (hasHeatPump) {
            return html`
                <div class="bg-info py-4-5 px-4 mb-4 tariff-description">
                    <h5 class="fw-bold mb-4">${TYPO3?.lang?.[`form.configureTariff.summary.hpAgreement.headline`]}</h5>

                    <ul class="mb-4 ps-4">
                        <li>
                            ${TYPO3?.lang?.[`form.configureTariff.summary.hpAgreement.info1`]}
                        </li>
                        <li>
                            ${TYPO3?.lang?.[`form.configureTariff.summary.hpAgreement.info2`]}
                        </li>
                        <li>
                            ${TYPO3?.lang?.[`form.configureTariff.summary.hpAgreement.info3`]}
                        </li>
                        <li>
                            ${TYPO3?.lang?.[`form.configureTariff.summary.hpAgreement.info4`]}
                        </li>
                    </ul>

                    <!--<p class="mb-0">
                        ${unsafeHTML(TYPO3?.lang?.[`form.configureTariff.summary.hpAgreement.infoLink`])}
                    </p>-->
                </div>

                <!-- Checkbox-Bestätigung -->
                <div class="form-check d-flex align-items-start mb-5">
                    <input class="form-check-input checkbox mt-0 checkbox-target" type="checkbox" id="hpAgreeCheck" name="heatPumpAgreement">
                    <label class="form-check-label ps-2" for="enfgConfirm">
                        ${TYPO3?.lang?.[`form.configureTariff.summary.hpAgreement.agreement`]}
                    </label>
                </div>
                <div class="invalid-feedback" id="hp-agreement-error"></div>
            `
        }
    }

    private renderModul2Agreement() {
        const tariffs = TariffFormStore.get('allTariffs') || [];
        const hasModul2Tariff = tariffs.some((tariff: any) => tariff.data?.tariff?.requires_modul2_agreement === true);

        if (hasModul2Tariff) {
            return html`
                <!-- Checkbox-Bestätigung -->
                <div class="form-check d-flex align-items-start mb-5">
                    <input class="form-check-input checkbox mt-0 checkbox-target" type="checkbox" id="modul2AgreeCheck" name="modul2Agreement">
                    <label class="form-check-label ps-2" for="modul2AgreeCheck">
                        ${TYPO3?.lang?.[`form.configureTariff.summary.modul2Agreement.agreement`]}
                    </label>
                </div>
                <div class="invalid-feedback" id="modul2-agreement-error"></div>
            `
        }
    }

    private renderRewards() {
        return html`
            <div class="w-100 d-flex mb-4 align-items-end">
                <h5 class="fw-bold m-0">${TYPO3?.lang?.[`form.configureTariff.summary.rewards.headline`]}</h5>
            </div>

            <div class="container p-0">
                <div class="row">
                    <div class="col-12">
                        <div class="form-floating mb-4">
                            <input type="text" class="form-control input-focus"
                                   id="promotionalCode"
                                   name="promotionalCode"
                                   placeholder="${TYPO3?.lang?.[`form.configureTariff.summary.rewards.promotionalCode`]}"/>
                            <label for="promotionalCode">${TYPO3?.lang?.[`form.configureTariff.summary.rewards.promotionalCode`]}</label>
                            <div class="invalid-feedback"></div>
                        </div>
                    </div>
                </div>
            </div>

            <div role="group" aria-label="Prämienauswahl">
                ${this.rewards.map(reward => html`
                    <div class="my-4 summary-card row mx-0">
                        <img src="${reward.media}" alt="${reward.title}"
                             class="img-fluid object-fit-cover object-pos-center-end col-12 col-md-4 mb-3 mb-md-0 p-0">
                        <div class="col-12 col-md-8">
                            <div class="form-check form-switch h-checkbox d-flex p-0 gap-2">
                                <div class="justify-content-between d-flex w-100 px-4 py-3 align-items-start">
                                    <label class="form-check-label font-18 fw-bold col-9" for="reward-${reward.uid}">
                                        ${reward.title}
                                    </label>
                                    <input
                                            class="form-check-input form-check-toggle col-3 checkbox-target"
                                            type="checkbox"
                                            name="reward"
                                            id="reward-${reward.uid}"
                                            .value="${reward.uid}"
                                            .checked="${this.selectedRewardUid === reward.uid}"
                                            @change="${(e: Event) => this._handleRewardChange(e, reward.uid)}"
                                    >
                                </div>
                            </div>
                            <p class="font-18 px-4 pe-md-0 pt-2">
                                ${unsafeHTML(reward.description)}
                            </p>
                        </div>
                    </div>
                `)}
            </div>

            <!-- AGB Checkbox -->
            <div class="mb-3 w-100 d-flex flex-column">
                <div class="form-check d-flex align-items-center">
                <input class="form-check-input checkbox mt-0 checkbox-target" type="checkbox" id="agbCheck" name="agbCheck">
                <label class="form-check-label fw-bold ps-2" for="agbCheck">
                    ${unsafeHTML(TYPO3?.lang?.[`form.configureTariff.summary.rewards.agb`])}
                </label>
            </div>
            <div class="invalid-feedback" id="agb-error"></div>
            </div>

            <div class="form-check mb-3 w-100 d-flex justify-content-start pt-1">
                <input class="form-check-input checkbox checkbox-target mt-0" type="checkbox" id="personal-advertising-news"
                       name="personal-advertising-news">
                <label class="form-check-label ps-2" for="personal-advertising-news">
                    ${TYPO3?.lang?.[`form.configureTariff.summary.rewards.personalAdvertising`]}
                </label>
            </div>

            <!-- Buttons -->
            <div class="w-100 d-grid my-4 d-flex flex-column flex-md-row justify-content-between">
                <button id="submitButton" class="btn btn-primary btn-continue order-md-2 m-auto mx-md-0 text-white" >
                    ${TYPO3?.lang?.[`btn.orderTariffs`]}
                </button>
                <a href="#" id="sws-nav-back-payment-button"
                   class="btn btn-link btn-back text-black order-md-1">${TYPO3?.lang?.[`btn.back.payment`]}</a>
            </div>
        `;
    }

    private _setSalutation(salutation: string): string {
        switch (salutation) {
            case 'm':
                return TYPO3?.lang?.[`form.configureTariff.userData.contact.salutation.option.mr`];
            case 'f':
                return TYPO3?.lang?.[`form.configureTariff.userData.contact.salutation.option.ms`];
            case 'd':
                return '';
            default:
                return '';
        }
    }
}
